import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { emailWithdraw, getUserdata } from "../../Redux/Action";
import { setLocalStorageItem } from "../../Utils/Helper";

export const WithdrawBank = ({
  isWithdrawBankOpen,
  setIsWithdrawBankOpen,
  amount,
  setAmount,
  setFinalBalance,
  setRefresh
}) => {
  const userData = useSelector((state) => state?.Login?.userData);
  const dispatch = useDispatch();

  const [formData, setFormData] = useState({
    name: "",
    accountNo: "",
    iban: "",
    ifsc: "",
    branchName: "",
    moneyPlantAccountNo: userData?.accountno,
    amount,
    method: "bank",
  });
  const [errors, setErrors] = useState({
    name: "",
    accountNo: "",
    branchName: "",
    amount: "",
  });

  // For confirmation modal
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    let cleanedValue = value;

    if (name === "name" || name === "branchName") {
      cleanedValue = value.replace(/[^a-zA-Z\s]/g, "");
    } else if (["amount", "accountNo"].includes(name)) {
      cleanedValue = value.replace(/[^0-9]/g, "");
    } else if (["iban", "ifsc"].includes(name)) {
      cleanedValue = value.replace(/[^a-zA-Z0-9]/g, "").toUpperCase();
    }

    setFormData((prev) => ({ ...prev, [name]: cleanedValue }));

    if (name === "amount") {
      setAmount(cleanedValue);
    }
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const validateForm = () => {
    let hasError = false;
    const newErrors = {};
    const requiredFields = ["name", "accountNo", "amount", "branchName"];

    requiredFields.forEach((field) => {
      if (!formData[field]?.trim()) {
        newErrors[field] = "This field is required";
        hasError = true;
      }
    });

    setErrors(newErrors);
    return !hasError;
  };

  const onSubmitClick = (e) => {
    e.preventDefault();
    if (validateForm()) {
      setShowConfirmModal(true);
    }
  };

  const handleConfirm = (saveAccount) => {
    setShowConfirmModal(false);

    // Prepare payload, add saveAccount only if true
    const payload = { ...formData };
    if (saveAccount) {
      payload.saveAccount = true;
    } else {
      // Ensure saveAccount is not present if user declines
      delete payload.saveAccount;
    }

    dispatch(
      emailWithdraw({
        payload,
        callback: (data) => {
          if (data?.meta?.code === 200 && !isNaN(Number(data?.data?.newBalance))) {
            // Just refresh to update accounts, balance is already handled in UI
            setRefresh(prev => !prev)
            setIsWithdrawBankOpen(false);
            setAmount("");
            setFormData((prev) => ({
              ...prev,
              name: "",
              accountNo: "",
              iban: "",
              ifsc: "",
              branchName: "",
              amount: "",
            }));
            
            // Fetch updated user data for accounts after a delay
            setTimeout(() => {
              dispatch(getUserdata());
              // Trigger another refresh to update the AddedAccounts component
              setRefresh(prev => !prev);
            }, 1500);
          } else {
            setIsWithdrawBankOpen(false);
            setAmount("");
            setRefresh(prev => !prev)
            
            // Still fetch user data to ensure consistency
            setTimeout(() => {
              dispatch(getUserdata());
              setRefresh(prev => !prev);
            }, 1500);
          }
        },
      })
    );
  };

  if (!isWithdrawBankOpen) return null;

  return (
    <>
      {/* Modal Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-40"
        onClick={() => setIsWithdrawBankOpen(false)}
      ></div>

      {/* Main Dialog */}
      <div className="fixed inset-0 flex items-center justify-center z-50 p-4">
        <div
          className="bg-white rounded-lg shadow-lg max-w-md w-full p-6 relative"
          onClick={(e) => e.stopPropagation()}
        >
          <h2 className="text-xl font-semibold mb-5 text-center">Withdraw Bank</h2>

          <form onSubmit={onSubmitClick} noValidate>
            <div className="space-y-4">
              <div>
                <label className="block font-medium mb-1" htmlFor="name">
                  Account Holder Name*
                </label>
                <input
                  id="name"
                  name="name"
                  type="text"
                  value={formData.name}
                  onChange={handleChange}
                  className={`w-full border rounded px-3 py-2 focus:outline-none focus:ring ${errors.name ? "border-red-500" : "border-gray-300"
                    }`}
                />
                {errors.name && (
                  <p className="text-red-500 text-sm mt-1">{errors.name}</p>
                )}
              </div>

              <div>
                <label className="block font-medium mb-1" htmlFor="accountNo">
                  Account No.*
                </label>
                <input
                  id="accountNo"
                  name="accountNo"
                  type="text"
                  value={formData.accountNo}
                  onChange={handleChange}
                  className={`w-full border rounded px-3 py-2 focus:outline-none focus:ring ${errors.accountNo ? "border-red-500" : "border-gray-300"
                    }`}
                />
                {errors.accountNo && (
                  <p className="text-red-500 text-sm mt-1">{errors.accountNo}</p>
                )}
              </div>

              <div>
                <label className="block font-medium mb-1" htmlFor="iban">
                  IBAN No.
                </label>
                <input
                  id="iban"
                  name="iban"
                  type="text"
                  value={formData.iban}
                  onChange={handleChange}
                  className="w-full border rounded px-3 py-2 focus:outline-none focus:ring border-gray-300"
                />
              </div>

              <div>
                <label className="block font-medium mb-1" htmlFor="ifsc">
                  Swift/BIC Code
                </label>
                <input
                  id="ifsc"
                  name="ifsc"
                  type="text"
                  value={formData.ifsc}
                  onChange={handleChange}
                  className="w-full border rounded px-3 py-2 focus:outline-none focus:ring border-gray-300"
                />
              </div>

              <div>
                <label className="block font-medium mb-1" htmlFor="branchName">
                  Bank Name*
                </label>
                <input
                  id="branchName"
                  name="branchName"
                  type="text"
                  value={formData.branchName}
                  onChange={handleChange}
                  className={`w-full border rounded px-3 py-2 focus:outline-none focus:ring ${errors.branchName ? "border-red-500" : "border-gray-300"
                    }`}
                />
                {errors.branchName && (
                  <p className="text-red-500 text-sm mt-1">{errors.branchName}</p>
                )}
              </div>

              <div>
                <label className="block font-medium mb-1" htmlFor="amount">
                  Amount*
                </label>
                <input
                  id="amount"
                  name="amount"
                  type="text"
                  value={amount}
                  onChange={handleChange}
                  className={`w-full border rounded px-3 py-2 focus:outline-none focus:ring ${errors.amount ? "border-red-500" : "border-gray-300"
                    }`}
                />
                {errors.amount && (
                  <p className="text-red-500 text-sm mt-1">{errors.amount}</p>
                )}
              </div>
            </div>

            <div className="mt-6 flex justify-center space-x-4">
              <button
                type="submit"
                className="bg-green-600 hover:bg-green-700 text-white px-6 py-2 rounded font-semibold focus:outline-none focus:ring"
              >
                Submit
              </button>

              <button
                type="button"
                onClick={() => setIsWithdrawBankOpen(false)}
                className="bg-gray-300 hover:bg-gray-400 text-gray-700 px-6 py-2 rounded font-semibold focus:outline-none focus:ring"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Confirmation Modal */}
      {showConfirmModal && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          onClick={() => setShowConfirmModal(false)}
        >
          <div
            className="bg-white rounded-xl shadow-2xl max-w-sm w-full p-8 relative"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              className="absolute top-2 right-2 text-gray-500 hover:text-gray-800 text-xl"
              onClick={() => setShowConfirmModal(false)}
            >
              &times;
            </button>
            <h3 className="text-xl font-bold mb-3 text-center text-gray-800">
              Save this bank account?
            </h3>
            <p className="mb-6 text-center text-gray-600 text-sm">
              Do you want to save this bank account for future use?
            </p>
            <div className="flex justify-center gap-4">
              <button
                onClick={() => handleConfirm(true)}
                className="bg-emerald-500 hover:bg-emerald-600 text-white px-6 py-2.5 rounded-full font-medium shadow-md transition duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-emerald-400"
              >
                Yes
              </button>
              <button
                onClick={() => handleConfirm(false)}
                className="bg-gray-200 hover:bg-gray-300 text-gray-800 px-6 py-2.5 rounded-full font-medium shadow-md transition duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-gray-300"
              >
                No
              </button>
            </div>
          </div>
        </div>
      )}

    </>
  );
};